import { Search } from 'lucide-react';
import { InputWithIcon } from '../common/Input';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@/configs/constants';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '../common/Popover';
import { useStore } from '@/hooks/useStore';
import { flowResult, toJS } from 'mobx';
import debounce from 'lodash.debounce';

interface UsersComboboxProps {
    className?: string,
    setOwnerName: (value: string) => void,
    setphoneNumber: (value: string) => void
}

interface Item {
    id: number,
    code: string,
    fullName: string,
    phoneNumber: string,
    createdAt?: string
}

export default observer(function UsersCombobox({ className = '', setOwnerName, setphoneNumber }: UsersComboboxProps) {
    // hooks
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const keyword = searchParams.get('keyword') || '';

    const [openPopoverSearch, setOpenPopoverSearch] = useState<boolean>(false);

    // state
    const [value, setValue] = useState<string>(keyword);

    const {
        userStore: { userscombobox, getUsersCombobox }
    } = useStore();

    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (inputWrapperRef.current) {
            setInputWidth(inputWrapperRef.current.offsetWidth);
        }
    }, [inputWrapperRef.current]);

    const debouncedSearch = useCallback(
        debounce(async (searchValue: string) => {
            const res = await getUsersCombobox(searchValue, 20, 1);
            setOpenPopoverSearch(true);
        }, 500),
        []
    );

    // function
    const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setValue(value);
        debouncedSearch(value);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearch = () => {
        if (keyword === value) {
        } else {
        }
    };

    const handleClickItem = (item: Item) => {
        setOwnerName(item?.code);
        setphoneNumber(item?.phoneNumber);
        setValue(item?.fullName);
        setOpenPopoverSearch(false);
    };

    return (
        <div className='relative flex items-center'>
            <div ref={inputWrapperRef} className='w-full'>
                <Popover open={openPopoverSearch}>
                    <PopoverTrigger className='w-full'>
                        <InputWithIcon
                            type='text'
                            placeholder={t('placeholder.choose')}
                            value={value}
                            onChange={onChange}
                            onKeyDown={handleKeyPress}
                            className={className}
                            maxLength={70}
                        />
                    </PopoverTrigger>
                    <PopoverContent
                        className={`p-1 max-h-[400px] overflow-auto ${inputWidth ? `!w-[${inputWidth}px]` : ''} `}
                        align='start'
                        autoFocus={false}
                        widthFull={true}
                        onOpenAutoFocus={e => e.preventDefault()}
                        onPointerDownOutside={() => setOpenPopoverSearch(false)}
                    >
                        <div className='divide-y divide-gray-200'>
                            {
                                (userscombobox && userscombobox.length > 0) ?
                                    userscombobox.map((item, index) => (
                                        <div
                                            key={index}
                                            className='py-2 px-1 text-sm flex items-center gap-2 hover:cursor-pointer hover:bg-slate-200'
                                            onClick={() => {
                                                handleClickItem(item);
                                            }}
                                            title={`${item.fullName} - ${item.phoneNumber}`}
                                        >
                                            <span className='truncate-text'>{`${item.fullName} - ${item.phoneNumber}`}</span>
                                        </div>
                                    )) :
                                    (
                                        <div
                                            className='py-2 px-1 text-sm flex items-center h-[100px]'
                                        >
                                            <div className='text-center w-full'>{t('sentences.no_user_found')}</div>
                                        </div>
                                    )
                            }
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
});
